<template>
  <div class="relative" :aria-busy="!ready">
    <div v-if="!ready" aria-hidden="true">
      <slot name="placeholder" />
    </div>
    <div
      ref="content"
      :class="ready ? '' : 'invisible absolute inset-0 pointer-events-none'"
      :inert="!ready"
      :aria-hidden="!ready"
    >
      <slot />
    </div>
  </div>
</template>
<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { waitForImages } from './waitForImages';

const props = defineProps<{ contentKey: unknown }>();
const content = ref<HTMLElement>();
const ready = ref(false);
let stop: (() => void) | undefined;

onMounted(() => {
  stop = watch(() => props.contentKey, async (_value, _oldValue, onCleanup) => {
    ready.value = false;
    const controller = new AbortController();
    let frame: number | undefined;
    onCleanup(() => {
      controller.abort();
      if (frame !== undefined) cancelAnimationFrame(frame);
    });

    // Keep the carousel mounted and measurable behind the placeholder.
    await nextTick();
    if (!content.value || controller.signal.aborted) return;
    await waitForImages(content.value, controller.signal);
    if (controller.signal.aborted) return;
    frame = requestAnimationFrame(() => {
      if (!controller.signal.aborted) ready.value = true;
    });
  }, { immediate: true, deep: true, flush: 'post' });
});

onBeforeUnmount(() => stop?.());
</script>
